import CheckIcon from '@mui/icons-material/Check';
import { Box, FormControlLabel, Radio, RadioGroup, Typography } from '@mui/material';
import type { Theme } from '@mui/material/styles';
import type { SystemStyleObject } from '@mui/system';
import React, { forwardRef } from 'react';

import { Neutrals, Primary } from '../../color-palette/colors';
import { FOCUS_RING, FOCUS_RING_OFFSET } from '../../focusRing';

import type { FieldBaseProps, SelectOption } from './field.types';
import { FieldShell, describedBy, labelledBy } from './FieldShell';
import { useFieldIds } from './useFieldIds';
import { visuallyHiddenSx } from './fieldStyles';

const CHIP_MIN_WIDTH = 104;

export const chipTextColor = (selected: boolean, disabled: boolean): string => {
  if (selected) return Primary[900];
  return disabled ? Neutrals[600] : Neutrals[700];
};

export type ChoiceChipFit = 'fill' | 'content';

interface ChoiceChipGroupOwnProps<T extends string> extends Omit<FieldBaseProps, 'disabled'> {
  options: readonly SelectOption<T>[];
  value: T;
  onChange: (value: T) => void;
  name?: string;
  columns?: number;
  columnOf?: (value: T) => number;
  fit?: ChoiceChipFit;
}

type ChoiceChipAvailability =
  | { frozen?: boolean; disabled?: never; locked?: never }
  | { frozen?: never; disabled?: boolean; locked?: boolean };

export type ChoiceChipGroupProps<T extends string> = ChoiceChipGroupOwnProps<T> & ChoiceChipAvailability;

type ChipHold = 'live' | 'frozen' | 'locked' | 'disabled';

interface HoldEffect {
  readonly fieldDisabled: boolean;
  readonly optionsHeld: boolean;
  readonly inert: boolean;
}

const HOLD_EFFECT: Readonly<Record<ChipHold, HoldEffect>> = {
  live: { fieldDisabled: false, optionsHeld: false, inert: false },
  frozen: { fieldDisabled: false, optionsHeld: false, inert: true },
  locked: { fieldDisabled: false, optionsHeld: true, inert: false },
  disabled: { fieldDisabled: true, optionsHeld: true, inert: false },
};

const holdOf = (disabled: boolean, locked: boolean, frozen: boolean): ChipHold => {
  if (disabled) return 'disabled';
  if (locked) return 'locked';
  return frozen ? 'frozen' : 'live';
};

type ChipLayout = 'chips' | 'columns' | 'rows';

const layoutOf = (columns: number | undefined): ChipLayout => {
  if (columns === undefined) return 'chips';
  return columns === 1 ? 'rows' : 'columns';
};

const gridTemplate = (optionCount: number, columns: number | undefined, fit: ChoiceChipFit): string => {
  if (columns !== undefined) return `repeat(${columns}, minmax(0, 1fr))`;
  if (fit === 'content') return `repeat(auto-fill, minmax(min(100%, ${CHIP_MIN_WIDTH}px), max-content))`;
  return optionCount > 1
    ? `repeat(auto-fit, minmax(min(100%, ${CHIP_MIN_WIDTH}px), 1fr))`
    : `minmax(min(100%, ${CHIP_MIN_WIDTH}px), max-content)`;
};

const groupSx = (template: string, inert: boolean): SystemStyleObject<Theme> => ({
  display: 'grid',
  gap: 2,
  gridTemplateColumns: template,
  justifyItems: 'stretch',
  pointerEvents: inert ? 'none' : undefined,
});

const borderOf = (selected: boolean, disabled: boolean): string => {
  if (selected) return Primary[900];
  return disabled ? Neutrals[300] : Neutrals[500];
};

interface ChipLook {
  readonly selected: boolean;
  readonly disabled: boolean;
  readonly layout: ChipLayout;
  readonly column: number | undefined;
}

const chipSx =
  ({ selected, disabled, layout, column }: ChipLook) =>
  (theme: Theme): SystemStyleObject<Theme> => ({
    position: 'relative',
    m: 0,
    minWidth: 0,
    gridColumn: column,
    justifyContent: layout === 'rows' ? 'flex-start' : 'center',
    minHeight: theme.controlHeight.medium,
    '@media (hover: hover) and (pointer: fine)': { minHeight: theme.controlHeight.small },
    paddingInline: layout === 'columns' ? 2 : 3,
    paddingBlock: 1,
    borderRadius: 2,
    border: '1px solid',
    borderColor: borderOf(selected, disabled),
    boxShadow: selected ? `inset 0 0 0 1px ${Primary[900]}` : 'none',
    backgroundColor: selected ? Primary[100] : theme.palette.background.paper,
    color: chipTextColor(selected, disabled),
    cursor: disabled ? 'not-allowed' : 'pointer',
    transition: theme.transitions.create(['background-color', 'border-color', 'color'], {
      duration: theme.transitions.duration.shortest,
    }),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
    ...(selected || disabled ? {} : { '@media (hover: hover)': { '&:hover': { borderColor: Neutrals[700] } } }),
    '&:focus-within:has(:focus-visible)': { outline: FOCUS_RING, outlineOffset: FOCUS_RING_OFFSET },
    '& .MuiFormControlLabel-label.Mui-disabled': { color: 'inherit' },
    '& .MuiFormControlLabel-label': {
      ...theme.typography.label,
      fontWeight: selected ? theme.typography.fontWeightBold : theme.typography.fontWeightMedium,
      color: 'inherit',
      textAlign: layout === 'rows' ? 'start' : 'center',
      width: '100%',
      whiteSpace: 'normal',
      textWrap: 'balance',
      lineHeight: theme.typography.lineHeightTight,
      overflowWrap: 'anywhere',
      hyphens: 'auto',
    },
  });

const nameRowSx: SystemStyleObject<Theme> = {
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'inherit',
  gap: 1,
  maxWidth: '100%',
};
const leadingCheckSx: SystemStyleObject<Theme> = { flexShrink: 0 };
const cornerCheckSx = (theme: Theme): SystemStyleObject<Theme> => ({
  position: 'absolute',
  insetBlockStart: theme.spacing(1),
  insetInlineEnd: theme.spacing(1),
});
const descriptionSx: SystemStyleObject<Theme> = { display: 'block', color: Neutrals[600] };

interface ChipLabelProps {
  readonly label: React.ReactNode;
  readonly description: React.ReactNode;
  readonly descriptionId: string;
  readonly selected: boolean;
  readonly layout: ChipLayout;
}

const ChipLabel = ({ label, description, descriptionId, selected, layout }: ChipLabelProps) => (
  <>
    <Box component="span" sx={nameRowSx}>
      {selected && layout !== 'columns' && <CheckIcon fontSize="small" sx={leadingCheckSx} />}
      <span>{label}</span>
    </Box>
    {description && (
      <Typography component="span" variant="label" id={descriptionId} aria-hidden sx={descriptionSx}>
        {description}
      </Typography>
    )}
    {selected && layout === 'columns' && <CheckIcon fontSize="inherit" sx={cornerCheckSx} />}
  </>
);

function ChoiceChipGroupInner<T extends string>(
  props: ChoiceChipGroupProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>,
) {
  const {
    id: idProp,
    label,
    hideLabel,
    helperText,
    describedById,
    error,
    required,
    hideRequiredMark,
    disabled = false,
    size = 'small',
    fullWidth = true,
    className,
    sx,
    options,
    value,
    onChange,
    locked = false,
    name,
    columns,
    columnOf,
    fit = 'fill',
    frozen = false,
  } = props;
  const ids = useFieldIds(idProp);
  const layout = layoutOf(columns);
  const hold = holdOf(disabled, locked, frozen);
  const { fieldDisabled, optionsHeld, inert } = HOLD_EFFECT[hold];

  return (
    <FieldShell
      ref={ref}
      kind="radiogroup"
      ids={ids}
      label={label}
      hideLabel={hideLabel}
      helperText={helperText}
      error={error}
      required={required}
      hideRequiredMark={hideRequiredMark}
      disabled={fieldDisabled}
      size={size}
      fullWidth={fullWidth}
      className={className}
      sx={sx}
    >
      <RadioGroup
        row
        name={name ?? ids.id}
        aria-labelledby={labelledBy(ids, label)}
        aria-invalid={error === true ? true : undefined}
        aria-disabled={hold === 'live' ? undefined : true}
        tabIndex={optionsHeld ? 0 : undefined}
        aria-describedby={describedBy(ids, helperText, describedById)}
        value={value}
        onChange={(_event, next) => {
          if (inert) return;
          const picked = options.find((option) => String(option.value) === next);
          if (picked) onChange(picked.value);
        }}
        sx={groupSx(gridTemplate(options.length, columns, fit), inert)}
      >
        {options.map((option, index) => {
          const optionDisabled = optionsHeld || Boolean(option.disabled);
          const selected = option.value === value;
          const descriptionId = `${ids.id}-option-${index}-description`;
          return (
            <FormControlLabel
              key={String(option.value)}
              value={option.value}
              disabled={optionDisabled}
              label={
                <ChipLabel
                  label={option.label}
                  description={option.description}
                  descriptionId={descriptionId}
                  selected={selected}
                  layout={layout}
                />
              }
              sx={chipSx({ selected, disabled: optionDisabled, layout, column: columnOf?.(option.value) })}
              control={
                <Radio
                  size={size}
                  disableRipple
                  sx={visuallyHiddenSx}
                  inputProps={option.description ? { 'aria-describedby': descriptionId } : undefined}
                />
              }
            />
          );
        })}
      </RadioGroup>
    </FieldShell>
  );
}

export const ChoiceChipGroup = forwardRef(ChoiceChipGroupInner) as <T extends string>(
  props: ChoiceChipGroupProps<T> & { ref?: React.ForwardedRef<HTMLDivElement> },
) => React.ReactElement;
